import { Phone, Mail, MapPin } from "lucide-react";
import { Reveal, PageHero } from "@/components/Common";
import EnquiryForm from "@/components/EnquiryForm";
import { COMPANY } from "@/data/site";

const WhatsAppGlyph = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const CONTACT_CARDS = [
  { icon: Phone, label: "Call Us", value: COMPANY.phone, href: COMPANY.phoneHref, testid: "contact-phone-card" },
  { icon: Mail, label: "Email Us", value: COMPANY.email, href: COMPANY.emailHref, testid: "contact-email-card" },
  { icon: null, label: "WhatsApp", value: "Chat with our team", href: COMPANY.whatsapp, testid: "contact-whatsapp-card" },
  { icon: MapPin, label: "Head Office", value: COMPANY.address, href: null, testid: "contact-address-card" },
];

const Contact = () => (
  <>
    <PageHero
      crumb="Home — Contact Us"
      title="Contact Us"
      subtitle="Reach our team by phone, WhatsApp or email — or send us a message and we will get back to you."
    />
    <section className="py-16 sm:py-20 lg:py-24 bg-[#FAFAFC]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {CONTACT_CARDS.map((card, i) => {
            const Icon = card.icon;
            const inner = (
              <>
                <div className="w-11 h-11 bg-navy flex items-center justify-center transition-colors group-hover:bg-gold">
                  {Icon ? <Icon size={19} className="text-gold transition-colors group-hover:text-navy" /> : <span className="text-gold transition-colors group-hover:text-navy"><WhatsAppGlyph size={19} /></span>}
                </div>
                <p className="mt-5 text-[11px] font-bold tracking-[0.2em] uppercase text-slate-400">{card.label}</p>
                <p className="mt-1.5 text-sm font-semibold text-navy break-words">{card.value}</p>
              </>
            );
            const cls = "group bg-white border border-slate-200 p-7 h-full block transition-all hover:border-gold hover:-translate-y-1 hover:shadow-lg hover:shadow-navy/5";
            return (
              <Reveal key={card.label} delay={i * 0.07}>
                {card.href ? (
                  <a href={card.href} target={card.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className={cls} data-testid={card.testid}>{inner}</a>
                ) : (
                  <div className={cls} data-testid={card.testid}>{inner}</div>
                )}
              </Reveal>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
          <Reveal>
            <div className="bg-white border border-slate-200 p-7 sm:p-10 h-full">
              <h2 className="font-heading text-2xl font-bold text-navy tracking-tight mb-2">Send Us a Message</h2>
              <p className="text-sm text-slate-500 mb-8">We typically respond within one working day.</p>
              <EnquiryForm mode="contact" />
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full min-h-[420px] border border-slate-200 relative overflow-hidden">
              <iframe
                title="Daksh Head Office — Sector 17-B, Chandigarh"
                src="https://www.google.com/maps?q=SCO+10+Sector+17-B+Chandigarh+India&output=embed"
                className="absolute inset-0 w-full h-full"
                loading="lazy"
                data-testid="contact-map"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  </>
);

export default Contact;
